import { useEffect, useRef, useState } from "react";
import { Modal } from "@suite/ui";
import {
  signatureFromCanvas,
  signatureFromImage,
  signatureFromText,
  type SavedSignature,
} from "./signatures.ts";

type Tab = "draw" | "type" | "upload" | "saved";

const INK = "#1c2a5e";
const SCRIPT_FONT = '"Caveat", "Segoe Script", "Brush Script MT", cursive';

/**
 * Create or pick a signature. Everything happens on a canvas in the page; the
 * result is a trimmed transparent PNG that becomes a stamp.
 */
export function SignatureModal({
  open,
  onOpenChange,
  saved,
  onUse,
  onDelete,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  saved: SavedSignature[];
  /** Called with the chosen artwork; `isNew` when it should be added to the saved list. */
  onUse: (signature: SavedSignature, isNew: boolean) => void;
  onDelete: (id: string) => void;
}) {
  const [tab, setTab] = useState<Tab>(saved.length > 0 ? "saved" : "draw");
  useEffect(() => {
    if (open) setTab(saved.length > 0 ? "saved" : "draw");
  }, [open, saved.length]);

  const use = async (signature: SavedSignature | Promise<SavedSignature | null> | null, isNew: boolean) => {
    const resolved = await signature;
    if (!resolved) return;
    onUse(resolved, isNew);
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Add a signature"
      description="Draw it, type it, or use a photo of one. It never leaves this device."
    >
      <div className="mb-4 flex flex-wrap gap-1 border-b border-rule pb-3">
        {(
          [
            ["saved", `Saved${saved.length > 0 ? ` (${saved.length})` : ""}`],
            ["draw", "Draw"],
            ["type", "Type"],
            ["upload", "Upload"],
          ] as [Tab, string][]
        ).map(([key, label]) =>
          key === "saved" && saved.length === 0 ? null : (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`h-8 rounded-full px-3.5 text-[12px] font-semibold transition-colors ${
                tab === key ? "bg-pine text-cream" : "text-soft hover:bg-pine/10 hover:text-pine"
              }`}
            >
              {label}
            </button>
          ),
        )}
      </div>

      {tab === "saved" ? (
        <SavedTab saved={saved} onUse={(s) => void use(s, false)} onDelete={onDelete} />
      ) : tab === "draw" ? (
        <DrawTab onUse={(s) => void use(s, true)} />
      ) : tab === "type" ? (
        <TypeTab onUse={(s) => void use(s, true)} />
      ) : (
        <UploadTab onUse={(s) => void use(s, true)} />
      )}
    </Modal>
  );
}

// ---- tabs -----------------------------------------------------------------

function SavedTab({
  saved,
  onUse,
  onDelete,
}: {
  saved: SavedSignature[];
  onUse: (s: SavedSignature) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <ul className="grid grid-cols-2 gap-3">
      {saved.map((s) => (
        <li key={s.id} className="group relative">
          <button
            type="button"
            onClick={() => onUse(s)}
            className="flex h-24 w-full items-center justify-center rounded-[10px] border border-rule bg-[#fbfaf5] p-3 transition-colors hover:border-pine"
          >
            <img src={s.dataUrl} alt="Saved signature" className="max-h-full max-w-full object-contain" />
          </button>
          <button
            type="button"
            aria-label="Delete this saved signature"
            onClick={() => onDelete(s.id)}
            className="absolute right-1.5 top-1.5 flex size-6 items-center justify-center rounded-full bg-surface text-muted opacity-0 shadow transition-opacity hover:text-danger group-hover:opacity-100 focus:opacity-100"
          >
            ×
          </button>
        </li>
      ))}
    </ul>
  );
}

function DrawTab({ onUse }: { onUse: (s: SavedSignature | null) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.round(rect.width * dpr);
    canvas.height = Math.round(rect.height * dpr);
    const ctx = canvas.getContext("2d")!;
    ctx.scale(dpr, dpr);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.4;
    ctx.strokeStyle = INK;
  }, []);

  const point = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    return [e.clientX - rect.left, e.clientY - rect.top] as const;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d")!;
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
    setDirty(false);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="relative">
        <canvas
          ref={canvasRef}
          className="block h-[180px] w-full touch-none rounded-[10px] border border-rule bg-[#fbfaf5]"
          onPointerDown={(e) => {
            e.currentTarget.setPointerCapture(e.pointerId);
            drawing.current = true;
            const ctx = e.currentTarget.getContext("2d")!;
            const [x, y] = point(e);
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.lineTo(x + 0.01, y);
            ctx.stroke();
            setDirty(true);
          }}
          onPointerMove={(e) => {
            if (!drawing.current) return;
            const ctx = e.currentTarget.getContext("2d")!;
            const [x, y] = point(e);
            ctx.lineTo(x, y);
            ctx.stroke();
          }}
          onPointerUp={() => {
            drawing.current = false;
          }}
          onPointerCancel={() => {
            drawing.current = false;
          }}
        />
        <span className="pointer-events-none absolute bottom-3 left-4 right-4 border-t border-dashed border-rule" aria-hidden="true" />
      </div>
      <div className="flex items-center justify-between">
        <button type="button" onClick={clear} className="link text-[12px] font-semibold text-muted">
          Clear
        </button>
        <UseButton disabled={!dirty} onClick={() => onUse(canvasRef.current ? signatureFromCanvas(canvasRef.current) : null)} />
      </div>
    </div>
  );
}

function TypeTab({ onUse }: { onUse: (s: Promise<SavedSignature | null>) => void }) {
  const [text, setText] = useState("");
  return (
    <div className="flex flex-col gap-3">
      <input
        type="text"
        value={text}
        autoFocus
        onChange={(e) => setText(e.target.value)}
        placeholder="Type your name"
        className="h-10 w-full rounded-[8px] border border-rule bg-[#fbfaf5] px-3 text-[14px] text-ink focus:border-teal-deep focus:outline-none focus:ring-2 focus:ring-teal/30"
      />
      <div
        className="flex h-[120px] items-center justify-center rounded-[10px] border border-rule bg-[#fbfaf5] px-4"
        style={{ fontFamily: SCRIPT_FONT, fontWeight: 700, color: INK, fontSize: 44 }}
      >
        <span className="truncate">{text || <span className="text-muted/50">Your signature</span>}</span>
      </div>
      <div className="flex justify-end">
        <UseButton disabled={!text.trim()} onClick={() => onUse(signatureFromText(text, SCRIPT_FONT, 700))} />
      </div>
    </div>
  );
}

function UploadTab({ onUse }: { onUse: (s: SavedSignature | null) => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [removeWhite, setRemoveWhite] = useState(true);
  const [preview, setPreview] = useState<SavedSignature | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!file) return;
    let cancelled = false;
    setBusy(true);
    signatureFromImage(file, removeWhite)
      .then((s) => {
        if (!cancelled) setPreview(s);
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [file, removeWhite]);

  return (
    <div className="flex flex-col gap-3">
      <label className="flex h-[120px] cursor-pointer items-center justify-center rounded-[10px] border-2 border-dashed border-teal/45 bg-teal/[0.06] px-4 text-center">
        {preview ? (
          <img src={preview.dataUrl} alt="Uploaded signature preview" className="max-h-full max-w-full object-contain" />
        ) : (
          <span className="text-[13px] text-soft">
            {busy ? "Preparing…" : "Choose a PNG or JPG of your signature"}
          </span>
        )}
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="sr-only"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
      </label>
      <label className="flex items-center gap-2 text-[13px] text-soft">
        <input
          type="checkbox"
          checked={removeWhite}
          onChange={(e) => setRemoveWhite(e.target.checked)}
          className="size-4 accent-pine"
        />
        Remove the white background
      </label>
      <div className="flex justify-end">
        <UseButton disabled={!preview || busy} onClick={() => onUse(preview)} />
      </div>
    </div>
  );
}

function UseButton({ disabled, onClick }: { disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex h-9 items-center gap-2 rounded-full border-2 border-teal-deep bg-teal px-4 text-[12px] font-semibold text-cream shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_2px_5px_-2px_rgba(31,63,57,0.6)] transition-opacity hover:bg-teal-deep disabled:opacity-40 disabled:hover:bg-teal"
    >
      Use this signature
    </button>
  );
}
